/**
 * Canonical role display vocabulary shared by both frontends — apps/web
 * (User Management) and apps/admin (User Scope Management). Previously this
 * block was duplicated byte-for-byte in each app's `utils/roleUtils.js`; it
 * lives here so the display name and pill styling for a role are defined once.
 *
 * The role enum is **three-valued** — `super_admin`, `admin`, `normal_user`.
 * The legacy per-workspace `dashmote_admin` role value was retired; it
 * is intentionally absent here, so it now displays as its raw string with the
 * default (Normal User) styling rather than a first-class role.
 *
 * ── Known vocabulary misalignment (do not "fix" ad hoc) ───────────────────
 * A single role appears as up to THREE different strings depending on layer:
 *   - backend / Firestore value : `super_admin`   (the only form the API accepts)
 *   - frontend form token       : `"Super User"`  (the invite/scope-picker option value)
 *   - human display label       : `"Super Admin"` (what these maps produce)
 * (…and the analogous `admin`/`"Admin"`, `normal_user`/`"Normal User"`.)
 * The maps below therefore key on *every* form a role can arrive as, so a
 * lookup succeeds whether the caller holds a backend value or a form token.
 * Collapsing the frontend token onto the backend value is a separate,
 * behavior-touching refactor that has to move together with the shared
 * ScopePicker. Do not unpick it one call site at a time.
 */

export interface RoleStyle {
  borderColor: string;
  textColor: string;
  backgroundColor: string;
}

/**
 * Map any role representation (backend snake_case or a frontend/display string)
 * to its human display name. Display names are NOT a mechanical transform of
 * the backend value ("Super User" → "Super Admin"), so the mapping is explicit.
 */
export const ROLE_DISPLAY_NAMES: Record<string, string> = {
  // Backend format (with underscores)
  super_admin: "Super Admin",
  admin: "Admin",
  normal_user: "Normal User",
  // Frontend / display format (with spaces)
  "Super User": "Super Admin",
  Admin: "Admin",
  "Normal User": "Normal User",
};

/**
 * Pill styling per role. Keyed by every string form a role can arrive as
 * (backend value, frontend token, and display label) so `getRoleStyle`
 * resolves regardless of which the caller holds.
 */
export const ROLE_STYLES: Record<string, RoleStyle> = {
  super_admin: {
    borderColor: "#9333EA",
    textColor: "#9333EA",
    backgroundColor: "#FAF4FF",
  },
  "Super User": {
    borderColor: "#9333EA",
    textColor: "#9333EA",
    backgroundColor: "#FAF4FF",
  },
  "Super Admin": {
    borderColor: "#9333EA",
    textColor: "#9333EA",
    backgroundColor: "#FAF4FF",
  },
  admin: {
    borderColor: "#0E49B5",
    textColor: "#0E49B5",
    backgroundColor: "#F5F8FC",
  },
  Admin: {
    borderColor: "#0E49B5",
    textColor: "#0E49B5",
    backgroundColor: "#F5F8FC",
  },
  normal_user: {
    borderColor: "#F79009",
    textColor: "#F79009",
    backgroundColor: "#FFFCF5",
  },
  "Normal User": {
    borderColor: "#F79009",
    textColor: "#F79009",
    backgroundColor: "#FFFCF5",
  },
};

/** Display name for a role value (any form). Falsy → "User"; unknown → passthrough. */
export const getRoleDisplayName = (roleValue?: string | null): string => {
  if (!roleValue) return "User";
  return ROLE_DISPLAY_NAMES[roleValue] || roleValue;
};

/** Pill style for a role value (any form). Falsy/unknown → the Normal User style. */
export const getRoleStyle = (roleValue?: string | null): RoleStyle => {
  if (!roleValue) return ROLE_STYLES["Normal User"];
  return ROLE_STYLES[roleValue] || ROLE_STYLES["Normal User"];
};
